import * as React from 'react';
import { cn } from '../../lib/cn';

export interface CodeProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Whether to render as inline code (`code`) or a block (`pre > code`).
   * @default true
   */
  inline?: boolean;
  className?: string;
  ref?: React.Ref<HTMLElement>;
  children?: React.ReactNode;
}

const baseStyles = 'font-mono text-sm';

const inlineStyles = 'px-1.5 py-0.5 rounded bg-muted text-foreground border border-border';

const blockStyles =
  'block p-4 rounded-lg bg-muted text-foreground border border-border overflow-x-auto';

/**
 * Normalizes a Ref<HTMLElement> (object ref or callback ref) into a single
 * RefCallback so the same prop can be forwarded to either the `<code>` or
 * `<pre>` element without a ref-type mismatch.
 */
function adaptRef(
  ref: React.Ref<HTMLElement> | undefined,
): React.RefCallback<HTMLElement> | undefined {
  if (!ref) {
    return undefined;
  }
  return (el: HTMLElement | null) => {
    // oxlint-disable-next-line anti-slop/no-runtime-typeof -- React.Ref is a bare callback|object union with no discriminant, so typeof-function is the only split available.
    if (typeof ref === 'function') {
      ref(el);
    } else {
      ref.current = el;
    }
  };
}

export function Code({ inline = true, className, ref, children, ...props }: CodeProps) {
  if (inline) {
    return (
      <code ref={ref} className={cn(baseStyles, inlineStyles, className)} {...props}>
        {children}
      </code>
    );
  }

  return (
    <pre ref={adaptRef(ref)} className={cn(blockStyles, className)} {...props}>
      <code className={baseStyles}>{children}</code>
    </pre>
  );
}
